/**
 * The three states an overview tile can be in, and the rule that separates
 * them (dashboard design rules §5: unconfigured ≠ empty ≠ zero).
 *
 * L0 is the altitude a screenshot gets taken at, so a tile has exactly three
 * possible appearances and they must never be confusable:
 *
 * 1. **Measured** — a number, toned by its own verdict. `Card variant="outline"`
 *    with a status accent. "5 alerts firing" in danger and "0 firing" in success
 *    are both measurements, and both belong here.
 * 2. **Dark** — no measurement was taken. `Card variant="subtle"` (a recessed
 *    fill), an em dash where the number would be, and a `Badge` naming why in a
 *    short phrase. This is the state that stops a dark board rendering as a green
 *    zero, which is the whole reason this module exists.
 * 3. **Pending** — the first fetch has not landed. The grid renders a
 *    `Skeleton` of the tile's own footprint, so nothing moves when data arrives.
 *
 * Fill answers "was this measured?", tone answers "how bad is it?", and the two
 * are deliberately orthogonal — a recessed amber tile means "nobody measured
 * this and someone needs to act", which is a different sentence from a solid
 * amber tile ("we measured, and it is degraded"). The tone scale for a dark
 * tile is fixed:
 *
 * - `neutral` — nothing is wrong and nothing was expected ("no sites monitored",
 *   "snapshot not collected yet").
 * - `warning` — a human action is pending somewhere else: a credential to
 *   rotate, a role to assign, a value to inject. **Not an outage**, and never
 *   toned as one.
 * - `danger` — a configuration or code fault that will not fix itself (a
 *   workspace that does not exist, a query the service rejects).
 *
 * That scale is the one `ACCESS_COPY` in `features/dynamics/status.ts` already
 * uses, which is why the Dynamics tiles can take their tone straight from it
 * instead of deriving a second opinion.
 */

import { CloudOff, type IconComponent } from "@norican/icons";
import type { StatisticTone } from "@norican/react";
import type { ReactNode } from "react";

import type { FeedState } from "./feed-state";

/** What a tile shows where a number would be, when there is no number. */
export const EM_DASH = "—";

/** Why a tile carries no measurement, in the fewest words that stay accurate. */
export interface DarkReason {
  /**
   * The short phrase on the badge — "403 unauthorised", "Awaiting credential".
   * Read on its own by someone scanning the row, so it names the *kind* of
   * problem rather than the symptom.
   */
  badge: string;
  /** Icon paired with the badge, so the state is never colour-alone (§3.2). */
  icon: IconComponent;
}

/** What one resolved tile shows. */
export interface TileContent {
  value: ReactNode;
  tone: StatisticTone;
  caption?: ReactNode;
  delta?: ReactNode;
  /** Optional inline trend, rendered under the statistic (cost tile only). */
  trend?: ReactNode;
  /**
   * Set when this tile carries no measurement at all. Its presence — not its
   * tone — is what recesses the card and suppresses the number.
   */
  dark?: DarkReason;
}

/**
 * A tile with nothing measured behind it.
 *
 * The value is always an em dash: never `0`, never "Not configured" in the
 * number slot, never a stale figure carried over. The badge and caption carry
 * the explanation instead.
 */
export function darkTile(
  reason: DarkReason,
  caption: ReactNode,
  tone: StatisticTone = "neutral",
): TileContent {
  return { value: EM_DASH, tone, caption, dark: reason };
}

/**
 * Resolve one feed into a tile, handling the two states every feed shares.
 *
 * A thrown query means the *NOVA* API did not answer — not that the monitored
 * service is down — so it renders dark rather than as a failure of the domain.
 * `undefined` data is the pre-first-response state and renders as a skeleton;
 * everything else is the board's own business, which is what `content` decides.
 */
export function resolveTile<T>(
  feed: FeedState<T>,
  content: (data: T) => TileContent,
): TileContent | "pending" {
  if (feed.isError) {
    return darkTile(
      { badge: "Feed unreachable", icon: CloudOff },
      "The NOVA API did not answer for this board",
      "warning",
    );
  }
  if (feed.data === undefined) {
    return "pending";
  }
  return content(feed.data);
}
